<template>
  <!-- The pledge cards' Total row, as the P0/P1a/P1b frames draw it: the label on the
       left and "$ amount" on the right, inside one box. DS Input cannot do this — its
       label sits above the box and its prefix is set off by a divider. Empty, it is an
       outlined field; with an amount, a cream fill; disabled (the other side of
       "one-time OR five months" is in use), a flat grey box. -->
  <div class="flex flex-col gap-[6px]">
    <label :class="[boxClass, stateClass]">
      <span>{{ label }}</span>
      <span class="flex items-center gap-[2px] text-[20px]">
        <span aria-hidden="true">$</span>
        <!-- Sized to its text: an invisible copy of the value sets the width. `size="1"`
             because an input's default size of 20 sets the grid's width instead, which
             pushed the "$" across to the label. -->
        <span class="inline-grid">
          <span class="invisible whitespace-pre [grid-area:1/1]">{{
            modelValue || placeholder
          }}</span>
          <input
            type="text"
            inputmode="decimal"
            size="1"
            data-compact-input
            :value="modelValue"
            :placeholder="placeholder"
            :name="name"
            :aria-label="ariaLabel"
            :aria-invalid="error ? 'true' : undefined"
            :disabled="disabled"
            class="[grid-area:1/1] w-full min-w-0 p-0 border-none bg-transparent text-right font-[inherit] text-[20px] font-extrabold text-inherit outline-none placeholder:text-campaign-empty-text disabled:cursor-not-allowed disabled:placeholder:text-grey-30"
            @input="onInput"
            @blur="$emit('blur', $event)"
          />
        </span>
      </span>
    </label>
    <p v-if="error" class="text-[14px] leading-[20px] text-status-error">{{ error }}</p>
  </div>
</template>

<script>
import { sanitizeAmountInput } from '@/utils/amount-input';

export default {
  name: 'PledgeTotalField',

  props: {
    modelValue: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: 'Total',
    },
    ariaLabel: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: '–',
    },
    error: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'blur'],

  data() {
    return {
      // `!h-auto !mb-0`: FormInput.vue's unscoped `label { height: 24px }` reaches
      // every <label> in the app. 11/15 inside a 1px border: the frames' 12/16 box.
      boxClass:
        'flex justify-between items-center gap-[12px] !h-auto !mb-0 px-[15px] py-[11px] rounded-[12px] border border-solid text-[16px] font-extrabold',
    };
  },

  computed: {
    stateClass() {
      if (this.disabled) return 'bg-grey-10 border-transparent text-grey-30 cursor-not-allowed';
      if (this.error) return 'border-status-error text-campaign-ink';
      if (String(this.modelValue).trim()) {
        return 'bg-campaign-highlight-soft border-transparent text-campaign-ink';
      }
      return 'border-campaign-empty-line text-campaign-empty-text focus-within:border-campaign-primary';
    },
  },

  methods: {
    // Trimmed as typed, in the field as well as the model: a trim that leaves the
    // model as it was re-renders nothing, and the refused character would stay.
    onInput(event) {
      const value = sanitizeAmountInput(event.target.value);
      event.target.value = value;
      this.$emit('update:modelValue', value);
    },
  },
};
</script>
